import React from 'react';
import { Check, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from './input';

export type SearchableSelectOption = {
  id: string;
  label: string;
};

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value?: string;
  onChange: (value: string) => void;
  onSearchChange?: (search: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  isLoading?: boolean;
  className?: string;
  disabled?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  onSearchChange,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search',
  emptyMessage = 'No results found',
  isLoading = false,
  className,
  disabled = false
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const selectedOption = options.find((option) => option.id === value);

  const filteredOptions = React.useMemo(() => {
    if (onSearchChange) {
      return options;
    }
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [options, searchQuery, onSearchChange]);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    onSearchChange?.(query);
  };

  const handleSelect = (optionId: string) => {
    onChange(optionId);
    setIsOpen(false);
    setSearchQuery('');
    onSearchChange?.('');
  };

  const handleClickOutside = React.useCallback((event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  React.useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div className={cn('relative w-full', className)} ref={containerRef}>
      <button
        type='button'
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-left text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          !selectedOption && 'text-muted-foreground'
        )}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
      >
        <span className='block truncate'>
          {selectedOption?.label || placeholder}
        </span>
        {isOpen ? (
          <ChevronUp className='h-4 w-4 opacity-50' />
        ) : (
          <ChevronDown className='h-4 w-4 opacity-50' />
        )}
      </button>

      {isOpen && (
        <div className='absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg'>
          <div className='relative p-2'>
            <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500' />
            <Input
              ref={inputRef}
              type='text'
              placeholder={searchPlaceholder}
              className='w-full rounded-md border py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring'
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>

          <div className='max-h-60 overflow-auto p-1'>
            {isLoading ? (
              <div className='px-2 py-2 text-sm text-gray-500'>Loading...</div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = option.id === value;
                return (
                  <div
                    key={option.id}
                    role='option'
                    aria-selected={isSelected}
                    onClick={() => handleSelect(option.id)}
                    className={cn(
                      'flex cursor-pointer items-center rounded-md px-2 py-2 text-sm',
                      isSelected ? 'bg-blue-50' : 'hover:bg-gray-100'
                    )}
                  >
                    <span className='flex-1'>{option.label}</span>
                    {isSelected && (
                      <Check className='h-4 w-4 text-blue-600' />
                    )}
                  </div>
                );
              })
            ) : (
              <div className='px-2 py-2 text-sm text-gray-500'>
                {emptyMessage}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
